import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { localeCourante } from '../i18n';
import {
  listerOperations, lirePreparation, listerVisitesPreparees, synchroniser, apiPourTenant,
  reessayerOperation, retirerOperation, EVENEMENT_FILE, lireReference, preparerInventaires,
} from '../services/offlineService';
import { apiGet } from '../utils/api';
import PreparationHorsLigneModal from './prevention/PreparationHorsLigneModal';

// Rythme des tentatives d'envoi tant que des enregistrements attendent dans l'appareil
// (l'événement « online » du navigateur n'est pas fiable sur un réseau faible).
const INTERVALLE_ENVOI_MS = 30 * 1000;

/**
 * Pastille réseau (en haut à droite) + envoi AUTOMATIQUE des enregistrements faits
 * hors ligne. Aucun bouton à cliquer : l'envoi part au retour du réseau, au retour
 * dans l'application et toutes les 30 s tant qu'il reste quelque chose à envoyer.
 */
const OfflineManager = ({ tenant }) => {
  const { t } = useTranslation();
  const { tenantSlug } = useTenant();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [operations, setOperations] = useState([]);
  const [preparation, setPreparation] = useState(null);
  const [nbPreparees, setNbPreparees] = useState(0);
  const [vehiculesGardes, setVehiculesGardes] = useState(null); // { nb, date }
  const [preparationInventaires, setPreparationInventaires] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreparation, setShowPreparation] = useState(false);
  const userId = user?.id;

  // Vérifier si le module prévention est actif
  const hasPreventionModule = tenant?.parametres?.module_prevention_active || false;

  const rafraichir = useCallback(async () => {
    if (!tenantSlug || !userId) return;
    try {
      const [ops, prep, visites, vehicules] = await Promise.all([
        listerOperations(tenantSlug, userId),
        lirePreparation(tenantSlug, userId),
        listerVisitesPreparees(tenantSlug, userId),
        lireReference(tenantSlug, userId, 'vehicules'),
      ]);
      setOperations(ops);
      setPreparation(prep);
      setNbPreparees(visites.length);
      setVehiculesGardes(vehicules ? { nb: (vehicules.valeur || []).length, date: vehicules.maj_le } : null);
    } catch (error) {
      console.error('Erreur lecture du stockage hors ligne:', error);
    }
  }, [tenantSlug, userId]);

  // Envoi de la file. `manuel` : affiche aussi le résultat quand il n'y avait rien à faire.
  const enCours = useRef(false);
  const envoyer = useCallback(async ({ manuel = false } = {}) => {
    if (!tenantSlug || !userId || enCours.current) return;
    if (!manuel && !navigator.onLine) return;
    enCours.current = true;
    try {
      const enAttente = (await listerOperations(tenantSlug, userId)).filter((o) => o.statut !== 'refusee');
      if (enAttente.length === 0) return;
      setSyncing(true);
      const bilan = await synchroniser(tenantSlug, userId, apiPourTenant(tenantSlug));
      if (bilan.envoyees > 0) {
        toast({ title: `✅ ${t('horsLigne.envoyesTitre')}`, description: t('horsLigne.envoyesDetail', { count: bilan.envoyees }) });
      }
      if (bilan.refusees > 0) {
        toast({ title: `⚠️ ${t('horsLigne.refusesTitre')}`, description: t('horsLigne.refusesDetail', { count: bilan.refusees }), variant: 'destructive' });
      }
      if (manuel && bilan.arretReseau) {
        toast({ title: t('horsLigne.toujoursSansReseau'), description: t('horsLigne.envoiAutomatique') });
      }
    } catch (error) {
      console.error('Erreur envoi hors ligne:', error);
    } finally {
      enCours.current = false;
      setSyncing(false);
      rafraichir();
    }
  }, [tenantSlug, userId, toast, t, rafraichir]);

  // Références stables : les écouteurs ne sont posés qu'une fois par compte connecté.
  const envoyerRef = useRef(envoyer);
  envoyerRef.current = envoyer;
  const rafraichirRef = useRef(rafraichir);
  rafraichirRef.current = rafraichir;

  useEffect(() => {
    const lancer = () => envoyerRef.current();
    const relire = () => rafraichirRef.current();
    relire();
    lancer(); // au démarrage : ce qui attendait depuis la dernière ouverture part tout de suite

    const handleOnline = () => {
      setIsOnline(true);
      lancer();
    };
    const handleOffline = () => setIsOnline(false);
    const handleVisible = () => {
      if (document.visibilityState === 'visible') lancer();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener(EVENEMENT_FILE, relire);
    document.addEventListener('visibilitychange', handleVisible);
    const minuterie = setInterval(lancer, INTERVALLE_ENVOI_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(EVENEMENT_FILE, relire);
      document.removeEventListener('visibilitychange', handleVisible);
      clearInterval(minuterie);
    };
  }, [tenantSlug, userId]);

  const enAttente = operations.filter((o) => o.statut !== 'refusee');
  const refusees = operations.filter((o) => o.statut === 'refusee');

  const preparerLesInventaires = async () => {
    setPreparationInventaires(true);
    try {
      // Code du module Actifs chargé maintenant : sans réseau, il ne pourrait plus l'être.
      await Promise.all([import('./GestionActifs'), import('./MesEPI'), import('./ModuleEPI'), import('./GestionInterventions'), import('./Remplacements'), import('./MesDisponibilites'), import('./Formations'), import('./Chat'), import('./Planning'), import('./Personnel'), import('./Batiments')]);
      const res = await preparerInventaires({ tenantSlug, userId, apiGet });
      toast({ title: `✅ ${t('horsLigne.inventairesPrets')}`, description: t('horsLigne.vehiculesGardes', { count: res.vehicules }) });
    } catch (error) {
      toast({ title: t('horsLigne.preparationEchouee'), description: error?.message || String(error), variant: 'destructive' });
    } finally {
      setPreparationInventaires(false);
      rafraichir();
    }
  };

  const retirer = async (op) => {
    if (!window.confirm(t('horsLigne.confirmerRetrait', { libelle: op.libelle || '' }))) return;
    await retirerOperation(op.id);
  };

  const reessayer = async (op) => {
    await reessayerOperation(op.id);
    envoyer({ manuel: true });
  };

  return (
    <>
      {/* Indicateur de statut (toujours visible, discret) */}
      <div style={{
        position: 'fixed',
        top: '70px',
        right: '20px',
        zIndex: 100000,
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
      }}>
        {/* Badge Online/Offline - Version discrète */}
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: isOnline ? '#28a745' : '#dc3545',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
          }}
          onClick={() => setShowModal(true)}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.3)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          title={isOnline ? `🟢 ${t('horsLigne.pastilleEnLigne')}` : `🔴 ${t('horsLigne.pastilleHorsLigne')}`}
        />

        {/* Enregistrements en attente d'envoi - Petit et discret */}
        {enAttente.length > 0 && (
          <div
            style={{
              background: '#ffc107',
              color: '#000',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 'bold',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              cursor: 'pointer'
            }}
            onClick={() => setShowModal(true)}
            title={t('horsLigne.enAttenteInfobulle', { count: enAttente.length })}
          >
            {enAttente.length}
          </div>
        )}

        {/* Envoi refusé par le serveur : à regarder */}
        {refusees.length > 0 && (
          <div
            style={{
              background: '#dc3545',
              color: '#fff',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 'bold',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              cursor: 'pointer'
            }}
            onClick={() => setShowModal(true)}
            title={t('horsLigne.refusesTitre')}
          >
            !
          </div>
        )}

        {/* Indicateur de synchronisation - Petit spinner */}
        {syncing && (
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid #0dcaf0',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} title={t('horsLigne.envoiEnCours')} />
        )}
      </div>

      {/* Style pour l'animation du spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Modal de gestion */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{hasPreventionModule ? `📱 ${t('horsLigne.titreGestion')}` : `🌐 ${t('horsLigne.titreIndicateur')}`}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {/* Statut actuel */}
              <div style={{
                background: isOnline ? '#d1f2eb' : '#f8d7da',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: `2px solid ${isOnline ? '#28a745' : '#dc3545'}`
              }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {isOnline ? `🟢 ${t('horsLigne.vousEtesEnLigne')}` : `🔴 ${t('horsLigne.vousEtesHorsLigne')}`}
                </div>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>
                  {isOnline ? t('horsLigne.statutEnLigne') : t('horsLigne.statutHorsLigne')}
                </div>
              </div>

              {/* Enregistrements en attente d'envoi */}
              {operations.length > 0 && (
                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px' }}>
                    ⏳ {t('horsLigne.enAttenteTitre', { count: enAttente.length })}
                  </h3>
                  <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
                    {operations.map((op) => (
                      <div key={op.id} style={{
                        background: 'white', padding: '8px 10px', borderRadius: '6px',
                        border: `1px solid ${op.statut === 'refusee' ? '#f5c2c7' : '#dee2e6'}`,
                      }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {op.statut === 'refusee' ? '❌' : '⏳'} {t(`horsLigne.type_${op.categorie || op.type}`)}{op.libelle ? ` — ${op.libelle}` : ''}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          {new Date(op.cree_le).toLocaleString(localeCourante(), { dateStyle: 'medium', timeStyle: 'short' })}
                        </div>
                        {op.statut === 'refusee' && (
                          <>
                            <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '4px' }}>
                              {t('horsLigne.refuseParServeur')} {op.derniere_erreur}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                              <Button size="sm" variant="outline" onClick={() => reessayer(op)}>{t('horsLigne.reessayer')}</Button>
                              <Button size="sm" variant="outline" onClick={() => retirer(op)} style={{ color: '#dc2626' }}>{t('horsLigne.retirer')}</Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  {enAttente.length > 0 && (
                    <Button
                      onClick={() => envoyer({ manuel: true })}
                      disabled={syncing}
                      variant="outline"
                      style={{ width: '100%', marginTop: '12px' }}
                    >
                      {syncing ? `🔄 ${t('horsLigne.envoiEnCours')}` : `🔄 ${t('horsLigne.envoyerMaintenant')}`}
                    </Button>
                  )}
                </div>
              )}

              {/* Inventaires de véhicules (tous les services) */}
              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '16px' }}>🚒 {t('horsLigne.inventairesTitre')}</h3>
                <div style={{ color: '#495057', marginBottom: '10px' }}>
                  {vehiculesGardes
                    ? t('horsLigne.vehiculesGardesLe', {
                      count: vehiculesGardes.nb,
                      date: new Date(vehiculesGardes.date).toLocaleString(localeCourante(), { dateStyle: 'medium', timeStyle: 'short' }),
                    })
                    : t('horsLigne.aucunVehiculeGarde')}
                </div>
                <Button
                  onClick={preparerLesInventaires}
                  disabled={!isOnline || preparationInventaires}
                  variant="outline"
                  style={{ width: '100%' }}
                >
                  {preparationInventaires ? `⏳ ${t('horsLigne.chargement')}` : `📥 ${t('horsLigne.preparerInventaires')}`}
                </Button>
                <p style={{ fontSize: '12px', color: '#6c757d', margin: '8px 0 0', lineHeight: '1.5' }}>
                  💡 {t('horsLigne.inventairesAide')}
                </p>
              </div>

              {/* Si module prévention pas actif */}
              {!hasPreventionModule && (
                <div style={{
                  background: '#fff3cd',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  💡 <strong>Info :</strong> {t('horsLigne.modulePreventionInactif')}
                </div>
              )}

              {/* Si module prévention actif, préparation des visites */}
              {hasPreventionModule && (
                <>
                  <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px' }}>
                    <div>📋 <strong>{t('horsLigne.visitesPrepareesStat')}</strong> {nbPreparees}</div>
                    {preparation && (
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '6px' }}>
                        📅 {t('horsLigne.dernierePreparation')} {new Date(preparation.date).toLocaleString(localeCourante(), { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => { setShowModal(false); setShowPreparation(true); }}
                    disabled={!isOnline}
                    style={{
                      width: '100%',
                      padding: '15px',
                      background: '#0dcaf0',
                      fontSize: '15px'
                    }}
                  >
                    📥 {t('horsLigne.preparerBouton')}
                  </Button>
                  <p style={{ fontSize: '12px', color: '#6c757d', margin: '5px 0 15px 0', lineHeight: '1.5' }}>
                    💡 {t('horsLigne.preparerAide')}
                  </p>

                  {/* Aide */}
                  <div style={{
                    marginTop: '10px',
                    padding: '12px',
                    background: '#e7f3ff',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}>
                    <strong>ℹ️ {t('horsLigne.commentCaMarche')}</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                      <li>{t('horsLigne.etape1')}</li>
                      <li>{t('horsLigne.etape2')}</li>
                      <li>{t('horsLigne.etape3')}</li>
                      <li>{t('horsLigne.etape4')}</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                {t('horsLigne.fermer')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPreparation && (
        <PreparationHorsLigneModal
          tenantSlug={tenantSlug}
          user={user}
          onClose={() => setShowPreparation(false)}
          onTermine={rafraichir}
        />
      )}
    </>
  );
};

export default OfflineManager;
